/**
 * Canvas plumbing of the timing views (W2-08 timing): device-pixel sizing with a ResizeObserver, redraws through
 * the app's FrameScheduler (requested on changes, every frame while playing), and the theme colours read from the
 * design tokens of the canvas element (render/timeline/theme: the same palette as the chart timeline, so the
 * waveform, grid and playhead look alike everywhere).
 */

import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import type { FrameScheduler } from '../../render/types';
import { readTimelineTheme, type TimelineTheme } from '../../render/timeline/theme';

export interface CanvasFrame {
  readonly ctx: CanvasRenderingContext2D;
  /** CSS pixel size (drawing uses CSS pixels; the context is scaled by the device pixel ratio). */
  readonly width: number;
  readonly height: number;
  readonly theme: TimelineTheme;
}

/**
 * Keeps `canvas` sized to its box and calls `draw` on the scheduler's frames: once after every `request()` (and
 * whenever `deps` change) and on every frame while `animate` is true.
 */
export function useCanvasView(
  scheduler: FrameScheduler,
  id: string,
  draw: (f: CanvasFrame) => void,
  animate: boolean,
): { readonly canvasRef: RefObject<HTMLCanvasElement | null>; readonly request: () => void } {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawRef = useRef(draw);
  const themeRef = useRef<TimelineTheme | null>(null);
  const animateRef = useRef(animate);
  useLayoutEffect(() => {
    drawRef.current = draw;
    animateRef.current = animate;
  });

  const requestRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    const paint = (): void => {
      const canvas = canvasRef.current;
      if (canvas === null) return;
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const dpr = typeof devicePixelRatio === 'number' && devicePixelRatio > 0 ? devicePixelRatio : 1;
      const pw = Math.round(width * dpr);
      const ph = Math.round(height * dpr);
      if (canvas.width !== pw) canvas.width = pw;
      if (canvas.height !== ph) canvas.height = ph;
      const ctx = canvas.getContext('2d');
      if (ctx === null) return;
      themeRef.current ??= readTimelineTheme(canvas);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawRef.current({ ctx, width, height, theme: themeRef.current });
    };
    const off = scheduler.add(id, 'overlay', () => {
      paint();
      if (animateRef.current) scheduler.request(id);
    });
    requestRef.current = () => {
      scheduler.request(id);
    };
    const canvas = canvasRef.current;
    let ro: ResizeObserver | null = null;
    if (canvas !== null && typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(() => {
        scheduler.request(id);
      });
      ro.observe(canvas);
    }
    // Theme changes (data-theme on the root) re-read the palette.
    let mo: MutationObserver | null = null;
    if (typeof MutationObserver === 'function' && typeof document !== 'undefined') {
      mo = new MutationObserver(() => {
        themeRef.current = null;
        scheduler.request(id);
      });
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class', 'style'] });
    }
    scheduler.request(id);
    return () => {
      off();
      ro?.disconnect();
      mo?.disconnect();
      requestRef.current = () => undefined;
    };
  }, [scheduler, id]);

  useEffect(() => {
    requestRef.current();
  });

  return {
    canvasRef,
    request: () => {
      requestRef.current();
    },
  };
}

/** Pixel-aligned vertical line. */
export function vline(ctx: CanvasRenderingContext2D, x: number, y0: number, y1: number, width = 1): void {
  const xx = Math.round(x) + (width % 2 === 1 ? 0.5 : 0);
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(xx, y0);
  ctx.lineTo(xx, y1);
  ctx.stroke();
}

/** Ruler label interval (s) giving at least `minPx` pixels between labels. */
export function rulerStep(spanSec: number, widthPx: number, minPx = 64): number {
  const steps = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300];
  for (const st of steps) if ((st / Math.max(1e-9, spanSec)) * widthPx >= minPx) return st;
  return 600;
}
